import React from 'react';
import Button from '../Button/Button';
import { useDispatch, useSelector } from 'react-redux';
import GuestAction from '../../store/actions/GuestAction';
import SupportAction from '../../store/actions/SupportAction';
import InterpreterAction from '../../store/actions/InterpreterAction';
import ServiceAreaAction from '../../store/actions/ServiceAreaAction';
import BUTTON_CONFIG from '../../constants/button.constants';
import { STORE_NAMES } from '../../constants/store.constants';
import { GUEST_AUDIO_INVITATION, MULTI_INVITE_TYPE, GUEST_PATIENT_VIDEO_INVITATION } from '../../constants/guest.constants';
import {
  INTERPRETER_VIDEO,
  INTERPRETER_AUDIO,
  INVITE_INTERPRETER_DIALOUT_ROLE,
  NCAL_REGION,
  AUDIO_ONLY,
  AUDIO_INTERPRETER,
  SCAL_REGION,
  LANNGUAGE_CODE
} from '../../constants/interpreter.constants';
import { INVITE_BUTTON_ID } from '../../constants/dom.constants';
import { ADD_INTERPRETER_KEY, ADD_GUEST_KEY, ADD_SUPPORT_KEY } from '../../constants/tab.constants';
import ZoomAppClient from '../../helper/zoomAppClient';
import { maskPhoneNumber } from '../../helper/commonUtils';

const InviteButton = () => {
  const dispatch = useDispatch();
  const { enableInviteButton } = useSelector(state => state?.[STORE_NAMES.INVITE_BUTTON]);
  const { unformattedPhoneNumber } = useSelector(state => state?.[STORE_NAMES.DIALPAD]);
  const { contact_number, sipUri } = useSelector(state => state?.[STORE_NAMES.INTERPRETER_AUDIO_INFO]);
  const { appConfig } = useSelector(state => state?.[STORE_NAMES.APP]);
  const zoomClientDetails = useSelector(state => state?.[STORE_NAMES.ZOOM_CLIENT]);
  const interpreterStoreData = useSelector(state => state?.[STORE_NAMES.INTERPRETER]);
  const {
    appointmentRegion: region = null,
    appointmentVisitType: visitType = null,
    provider: { nuid = '' } = {},
    appointmentId = null
  } = useSelector(state => state?.[STORE_NAMES.APPOINTMENT_DETAILS]);
  const guestStoreData = useSelector(state => state?.[STORE_NAMES.GUEST]);
  const { currentTabContext } = useSelector(state => state?.[STORE_NAMES.TABS]);
  const { currentSupportTab } = useSelector(state => state?.[STORE_NAMES.SUPPORT_TAB_INFO]);
  const supportDetailsStoreData = useSelector(state => state?.[STORE_NAMES.SUPPORT_TAB_INFO]);
  const isNcalRegion = region?.trim?.().toUpperCase() === NCAL_REGION;
  const normalizedRegion = region?.trim?.().toUpperCase();

  const buildInterpreterVideoInvitationPayload = () => {
    const { language: { languageName = '', sipUri = '', languageCode = '' } = {} } = interpreterStoreData;
    const zoomPayloadForVideo = prepareZoomDetailsPayload(zoomClientDetails, INTERPRETER_VIDEO);

    // Align interpreter invite payload with AMN spec, including NCAL-specific fields.
    const videoInterpreter = {
      languageName: languageName,
      languageCode: isNcalRegion ? languageCode : languageCode || undefined,
      mode: INTERPRETER_VIDEO.toUpperCase()
    };
    if (!isNcalRegion) {
      videoInterpreter.sipUri = sipUri;
    }

    return {
      ...zoomPayloadForVideo,
      region: region,
      visitType: visitType,
      nuid: nuid,
      interpreter: {
        ...videoInterpreter
      }
    };
  };

  const buildInterpreterAudioInvitationPayload = () => {
    const zoomPayloadForAudio = prepareZoomDetailsPayload(zoomClientDetails, INTERPRETER_AUDIO);

    // Audio interpreter payload uses setup SIP for non-NCAL, and clientId as languageCode for NCAL.
    const audioInterpreter = {
      languageName: isNcalRegion ? AUDIO_ONLY : AUDIO_INTERPRETER,
      mode: INTERPRETER_AUDIO.toUpperCase()
    };
    if (!isNcalRegion) {
      audioInterpreter.sipUri = sipUri || (typeof contact_number === 'string' ? contact_number : '');
    }
    if (isNcalRegion) {
      audioInterpreter.languageCode = LANNGUAGE_CODE; // Use generic language code for audio-only interpreters in NCAL
    }

    return {
      ...zoomPayloadForAudio,
      region: region,
      visitType: visitType,
      nuid: nuid,
      interpreter: {
        ...audioInterpreter
      }
    };
  };

  const buildGuestAudioInvitationPayload = () => {
    const dialOutNumber = interpreterStoreData?.serviceArea?.number ?? unformattedPhoneNumber;
    const dialoutName = interpreterStoreData?.serviceArea?.name ?? '';
    const zoomPayloadForAudio = prepareZoomDetailsPayload(zoomClientDetails, INTERPRETER_AUDIO);

    return {
      ...zoomPayloadForAudio,
      region: region,
      visitType: visitType,
      dialout: {
        number: `${dialOutNumber ?? ''}`,
        name: dialoutName,
        role: INVITE_INTERPRETER_DIALOUT_ROLE
      },
      nuid
    };
  };

  const buildGuestPatientVideoInvitationPayload = () => {
    const zoomPayloadForMultiGuestInvite = prepareZoomDetailsPayload(zoomClientDetails, GUEST_PATIENT_VIDEO_INVITATION);
    const { multiGuestPatientInvitees } = guestStoreData;

    return {
      ...zoomPayloadForMultiGuestInvite,
      region: region,
      visitType: visitType,
      invitee: multiGuestPatientInvitees,
      nuid
    };
  };

  const prepareInvitationPayload = context => {
    const invitationPayloadBuilder = {
      [INTERPRETER_VIDEO]: buildInterpreterVideoInvitationPayload,
      [INTERPRETER_AUDIO]: buildInterpreterAudioInvitationPayload,
      [GUEST_AUDIO_INVITATION]: buildGuestAudioInvitationPayload,
      [GUEST_PATIENT_VIDEO_INVITATION]: buildGuestPatientVideoInvitationPayload
    }[context];

    return invitationPayloadBuilder ? invitationPayloadBuilder() : {};
  };

  const prepareZoomDetailsPayload = (zoomClientDetailsPayload, invitationContext) => {
    const { meetingID, meetingJoinUrl, userContext } = zoomClientDetailsPayload;
    const { screenName, participantUUID } = userContext;
    const payload = {
      zoomMeetingId: meetingID,
      zoomMeetingParticipantId: participantUUID,
      zoomMeetingUrl: meetingJoinUrl,
      zoomMeetingParticipantName: screenName
    };
    if (invitationContext == INTERPRETER_VIDEO) {
      const { zoomMeetingUrl, ...interpreterVideoZoomDetails } = payload;
      return interpreterVideoZoomDetails;
    } else if (invitationContext == INTERPRETER_AUDIO) {
      const { zoomMeetingUrl, ...interpreterAudioZoomDetails } = payload;
      return interpreterAudioZoomDetails;
    } else if (invitationContext == GUEST_PATIENT_VIDEO_INVITATION) {
      const { zoomMeetingParticipantName, ...guestPatientVideoDetails } = payload;
      return guestPatientVideoDetails;
    } else if (invitationContext === 'support') {
      const { zoomMeetingUrl, ...supportZoomDetails } = payload;
      return supportZoomDetails;
    }
  };

  const prepareSnowPayload = () => {
    const { selectedSnowContactPhoneNumber = null, selectedSnowCategory = null, selectedSnowDescription = null } = supportDetailsStoreData;

    const { browserOSInfo = null } = zoomClientDetails;
    const zoomPayloadForSnow = prepareZoomDetailsPayload(zoomClientDetails, 'support');
    let snowPayload = {
      ...zoomPayloadForSnow,
      region: region,
      visitType: visitType,
      appointmentId: appointmentId,
      snowTicket: {
        userNuid: nuid,
        category: selectedSnowCategory,
        description: selectedSnowDescription,
        providerContactNumber: selectedSnowContactPhoneNumber,
        ...browserOSInfo
      }
    };
    return snowPayload;
  };

  const submitInterpreterInvite = interpreterType => {
    if (interpreterType === INTERPRETER_VIDEO) {
      const payload = prepareInvitationPayload(INTERPRETER_VIDEO);
      dispatch(InterpreterAction.postInterpreterAction(payload, appConfig));
      return;
    }

    if (interpreterType !== INTERPRETER_AUDIO) {
      return;
    }

    const payload = prepareInvitationPayload(INTERPRETER_AUDIO);
    if (normalizedRegion === SCAL_REGION && payload?.interpreter) {
      payload.interpreter.sipUri = interpreterStoreData?.serviceArea?.number;
    }
    dispatch(InterpreterAction.postInterpreterAction(payload, appConfig));
  };

  const submitGuestInvite = () => {
    const { type: guestInviteType } = guestStoreData;
    if (guestInviteType === MULTI_INVITE_TYPE) {
      const payload = prepareInvitationPayload(GUEST_PATIENT_VIDEO_INVITATION);
      dispatch(GuestAction.postGuestPatientAction(payload));
      return;
    }

    const payload = prepareInvitationPayload(GUEST_AUDIO_INVITATION);
    const invitationDetails = { forServiceArea: false, appConfig, contact_number: unformattedPhoneNumber };
    initiatePhoneCall({ inviteAudioPayload: payload, invitationDetails, phoneUserName: maskPhoneNumber(unformattedPhoneNumber) });
  };

  const submitSupportInvite = () => {
    const payload = prepareSnowPayload();
    dispatch(SupportAction.postSupportAction(payload));
  };

  const onInviteButtonClick = () => {
    if (currentTabContext === ADD_INTERPRETER_KEY) {
      const { type: interpreterType = '' } = interpreterStoreData;
      submitInterpreterInvite(interpreterType);
      return;
    }

    if (currentTabContext === ADD_GUEST_KEY) {
      submitGuestInvite();
      return;
    }

    if (currentTabContext === ADD_SUPPORT_KEY) {
      submitSupportInvite();
    }
  };

  const initiatePhoneCall = async ({ inviteAudioPayload, invitationDetails, phoneUserName }) => {
    const { forServiceArea, contact_number, appConfig } = invitationDetails;
    dispatch(ServiceAreaAction.prePhoneCallDispatch({ forServiceArea, contact_number, appConfig }));
    const {
      dialout: { number: dialoutNumber }
    } = inviteAudioPayload;
    const makeZoomPhoneCallParams = ZoomAppClient.prepareZoomPhoneCallParams(dialoutNumber, { phoneUserName });
    const success = await ZoomAppClient.initiatePhoneCallAndRespond(makeZoomPhoneCallParams);
    if (!success) {
      dispatch(ServiceAreaAction.errorPhoneCallDispatch());
      return;
    }
    dispatch(ServiceAreaAction.successPhoneCallDispatch(inviteAudioPayload, forServiceArea));
  };

  return (
    <div className="vve-invite-btn-container">
      <Button disabled={!enableInviteButton} onClick={onInviteButtonClick} className="btn btn-primary btn-lg" id={INVITE_BUTTON_ID}>
        {currentSupportTab && currentTabContext === ADD_SUPPORT_KEY ? 'Submit' : BUTTON_CONFIG.INVITE_BUTTON.title}
      </Button>
    </div>
  );
};

export default InviteButton;
